import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Status/label chip.
 *
 * `tone` is semantic, `variant` is how loud. Keeping them separate is what lets
 * a table use `soft` everywhere for calm rows while a detail page's header uses
 * `solid` for the same status without inventing a second colour vocabulary.
 *
 * A `dot` is available because colour alone must never be the signal — the
 * shape gives colour-blind users and greyscale printouts something to read.
 */
const badgeVariants = cva(
  'inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-pill font-semibold leading-none',
  {
    variants: {
      tone: {
        neutral: '',
        primary: '',
        success: '',
        warning: '',
        info: '',
        destructive: '',
      },
      variant: {
        soft: '',
        solid: '',
        outline: 'bg-transparent border',
      },
      size: {
        sm: 'px-1.5 py-0.5 text-micro',
        md: 'px-2 py-0.5 text-xs',
        lg: 'px-2.5 py-1 text-sm',
      },
    },
    compoundVariants: [
      // soft — the table default. Tinted background, saturated text.
      { variant: 'soft', tone: 'neutral', class: 'bg-muted text-muted-foreground' },
      { variant: 'soft', tone: 'primary', class: 'bg-primary/12 text-primary' },
      { variant: 'soft', tone: 'success', class: 'bg-success/14 text-success' },
      { variant: 'soft', tone: 'warning', class: 'bg-warning/16 text-warning' },
      { variant: 'soft', tone: 'info', class: 'bg-info/14 text-info' },
      { variant: 'soft', tone: 'destructive', class: 'bg-destructive/12 text-destructive' },
      // solid — one per view at most, for the thing the page is about.
      { variant: 'solid', tone: 'neutral', class: 'bg-secondary text-secondary-foreground' },
      { variant: 'solid', tone: 'primary', class: 'bg-primary text-primary-foreground' },
      { variant: 'solid', tone: 'success', class: 'bg-success text-success-foreground' },
      { variant: 'solid', tone: 'warning', class: 'bg-warning text-warning-foreground' },
      { variant: 'solid', tone: 'info', class: 'bg-info text-info-foreground' },
      { variant: 'solid', tone: 'destructive', class: 'bg-destructive text-destructive-foreground' },
      // outline — for counts and filter chips, where a fill would compete
      // with the row content behind it.
      { variant: 'outline', tone: 'neutral', class: 'border-border text-muted-foreground' },
      { variant: 'outline', tone: 'primary', class: 'border-primary/40 text-primary' },
      { variant: 'outline', tone: 'success', class: 'border-success/40 text-success' },
      { variant: 'outline', tone: 'warning', class: 'border-warning/40 text-warning' },
      { variant: 'outline', tone: 'info', class: 'border-info/40 text-info' },
      { variant: 'outline', tone: 'destructive', class: 'border-destructive/40 text-destructive' },
    ],
    defaultVariants: { tone: 'neutral', variant: 'soft', size: 'md' },
  },
);

const dotTone: Record<string, string> = {
  neutral: 'bg-muted-foreground',
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
  destructive: 'bg-destructive',
};

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Status dot, so the state is not carried by colour alone. */
  dot?: boolean;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    { className, tone = 'neutral', variant = 'soft', size = 'md', dot = false, children, ...props },
    ref,
  ) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ tone, variant, size }), className)}
      {...props}
    >
      {dot && (
        <span
          aria-hidden="true"
          className={cn(
            'h-1.5 w-1.5 shrink-0 rounded-full',
            variant === 'solid' ? 'bg-current opacity-80' : dotTone[tone ?? 'neutral'],
          )}
        />
      )}
      {/*
        A plain label gets min-w-0 + truncate: a long free-text one (a rejection
        reason, a city nobody expected) must shrink rather than wrap the pill
        onto a second line and shove the row taller.

        Anything else — an icon beside a label, most often — is left as a direct
        child of the badge's own flex line. Inside the truncating span it was
        inline content, so an svg sat on the text baseline and rode up above the
        label, which is what "Rx" and "Prescription" were doing.
      */}
      {typeof children === 'string' || typeof children === 'number' ? (
        <span className="min-w-0 truncate">{children}</span>
      ) : (
        children
      )}
    </span>
  ),
);
Badge.displayName = 'Badge';

export { Badge };

export { badgeVariants };
